import {makeAutoObservable} from "mobx";
import AuthService from "../services/AuthService";

export default class Store {
    user = {}
    isLoading = true
    isAuth = false
    isError = false
    errorMessage = ""
    // 2FA: логін+пароль пройдено, чекаємо email-код (challengeId — ідентифікатор
    // challenge у Redis бекенду; сесії ще НЕМає)
    twoFactorRequired = false
    twoFactorChallengeId = null
    // Замасковані адреси, на які пішов код (з відповіді бекенду) — показуємо
    // у вікні вводу, щоб було видно, куди саме дивитись
    twoFactorSentTo = []

    constructor() {
        makeAutoObservable(this)
    }

    setAuth(boolean) {
        this.isAuth = boolean
    }

    setTwoFactor(required, challengeId = null, sentTo = []) {
        this.twoFactorRequired = required
        this.twoFactorChallengeId = challengeId
        this.twoFactorSentTo = Array.isArray(sentTo) ? sentTo : []
    }

    setLoading(boolean) {
        this.isLoading = boolean
    }

    setError(boolean, message = "") {
        this.isError = boolean
        this.errorMessage = message
    }

    setUser(userInfo) {
        this.user = userInfo
    }

    logOff() {
        this.isAuth = false
        this.user = {}
    }

    async login(username, password) {
        this.setLoading(true)
        this.setError(false)
        try {
            const response = await AuthService.login(username, password)
            // 2FA: пароль вірний, сесії ще немає — відкриваємо вікно вводу коду
            if (response.data?.requires2fa) {
                this.setTwoFactor(true, response.data.challengeId, response.data.sentTo)
                return
            }
            this.setAuth(true)
            this.setUser(response.data)
        } catch (error) {
            this.setAuth(false)
            this.setError(true, `${error?.response?.data?.message ? error.response.data.message : 'Сервер тимчасово недоступний'}`)
        } finally {
            this.setLoading(false)
        }
    }

    // 2FA: перевірка email-коду. Успіх → повноцінна авторизація (cookie вже
    // встановлено бекендом). challengeExpired у відповіді помилки → challenge
    // вичерпано/прострочено, повертаємось до кроку логін/пароль.
    async verifyTwoFactor(code) {
        this.setError(false)
        try {
            const response = await AuthService.loginVerify(this.twoFactorChallengeId, code)
            this.setTwoFactor(false, null)
            this.setAuth(true)
            this.setUser(response.data)
            return true
        } catch (error) {
            if (error?.response?.data?.challengeExpired) {
                this.setTwoFactor(false, null)
            }
            this.setError(true, `${error?.response?.data?.message ? error.response.data.message : 'Сервер тимчасово недоступний'}`)
            return false
        }
    }

    // 2FA: повторна відправка коду (бекенд тримає cooldown і ліміт resend-ів)
    async resendTwoFactorCode() {
        this.setError(false)
        try {
            await AuthService.loginResend(this.twoFactorChallengeId)
            return true
        } catch (error) {
            if (error?.response?.data?.challengeExpired) {
                this.setTwoFactor(false, null)
            }
            this.setError(true, `${error?.response?.data?.message ? error.response.data.message : 'Сервер тимчасово недоступний'}`)
            return false
        }
    }

    // 2FA: скасування вводу коду — challenge сам зникне у Redis по TTL
    cancelTwoFactor() {
        this.setTwoFactor(false, null)
    }

    // Після успішної примусової зміни пароля (ForcedPasswordChangeModal)
    clearPasswordChangeRequired() {
        this.user = { ...this.user, password_change_required: false }
    }

    async logout() {
        this.setLoading(true)
        try {
            await AuthService.logout()
        } catch (error) {
        } finally {
            this.setUser({})
            this.setAuth(false)
            this.setLoading(false)
        }
    }

    async checkAuth() {
        try {
            const response = await AuthService.checkAuth()
            this.setAuth(true)
            this.setUser(response.data)
        } catch (error) {
        } finally {
            this.setLoading(false)
        }
    }
}